import type { HighlightLanguage } from '../HighlightExtended.js';

const DEFAULT_FILE_NAME_STEM = 'code-snippet';
const DEFAULT_EXTENSION = '.txt';

/**
 * List of supported languages and their respective file extensions of the CodeSnippet.
 * @internal
 */
export const LANGUAGE_EXTENSION_MAP: Record<HighlightLanguage, string> = {
  markup: '.html',
  bash: '.sh',
  clike: '.txt',
  c: '.c',
  cpp: '.cpp',
  css: '.css',
  javascript: '.js',
  jsx: '.jsx',
  coffeescript: '.coffee',
  actionscript: '.as',
  go: '.go',
  graphql: '.graphql',
  json: '.json',
  markdown: '.md',
  objectivec: '.m',
  python: '.py',
  reason: '.re',
  sql: '.sql',
  tsx: '.tsx',
  typescript: '.ts',
  yaml: '.yaml',
  dql: '.dql',
  log: '.log',
  kotlin: '.kt',
  swift: '.swift',
  groovy: '.groovy',
};

/**
 * Derives the file extension (including the leading dot) for a given highlight
 * language. We fall back to `.txt` when the language has no direct file-extension
 * convention, when it isn't part of the map at all, or when no language is given.
 * @param language - The syntax-highlighting language of the snippet, if it's set.
 * @internal
 */
export function getFileExtension(language?: HighlightLanguage): string {
  if (!language) {
    return DEFAULT_EXTENSION;
  }

  // The language value could be outside of the `HighlightLanguage` union,
  // e.g. from a markdown fence casting an arbitrary info string,
  // so we fall back gracefully.
  return LANGUAGE_EXTENSION_MAP[language] ?? DEFAULT_EXTENSION;
}

/**
 * Resolves the file name to use when downloading the snippet.
 * Falls back to `code-snippet.<ext>` when `fileName` is omitted or produces
 * an empty name after sanitization.
 * @param fileName - Consumer-provided file name, with or without an extension.
 * @param language - The snippet's highlight language, used to derive the extension
 *   when `fileName` carries none or when sanitization results in an empty extension.
 * @internal
 */
export function resolveDownloadFileName(
  fileName: string | undefined,
  language: HighlightLanguage | undefined,
): string {
  const derivedExtension = getFileExtension(language);

  if (!fileName) {
    return `${DEFAULT_FILE_NAME_STEM}${derivedExtension}`;
  }

  const lastDotIndex = fileName.lastIndexOf('.');
  const hasExplicitExtension =
    lastDotIndex !== -1 && lastDotIndex !== fileName.length - 1;

  const stem = lastDotIndex === -1 ? fileName : fileName.slice(0, lastDotIndex);
  const rawExtension = hasExplicitExtension
    ? fileName.slice(lastDotIndex + 1)
    : undefined;

  const safeCharsPattern = /[^a-zA-Z0-9_-]/g;
  const sanitizedStem = stem.replace(safeCharsPattern, '');
  const sanitizedExtension = rawExtension?.replace(safeCharsPattern, '');

  const extension = sanitizedExtension
    ? `.${sanitizedExtension}`
    : derivedExtension;
  return `${sanitizedStem || DEFAULT_FILE_NAME_STEM}${extension}`;
}
